import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, useCallback, useEffect, useState, type JSX } from 'react';
import { useBlocker } from 'react-router-dom';
import { Abtretungserklaerung } from '../../components/Abtretungserklaerung';
import { AngebotZurAbtretung } from '../../components/AngebotZurAbtretung';
import { Auftragsbestaetigung } from '../../components/Auftragsbestaetigung';
import { AuftragZurErstellungFahrzeugbewertung } from '../../components/AuftragZurErstellungFahrzeugbewertung';
import { AuftragZurErstellungGutachten } from '../../components/AuftragZurErstellungGutachten';
import { AuftragZurErstellungReparaturbestaetigung } from '../../components/AuftragZurErstellungReparaturbestaetigung';
import { ERROR_MESSAGE, SUCCESS_MESSAGE_AUTO_HIDE } from '../../components/common/Alert';
import Formular from '../../components/common/Formular';
import { Signature } from '../../components/common/Signature';
import { WarningDialog } from '../../components/dialog/WarningDialog';
import { DSGVO } from '../../components/DSGVO';
import { DSGVOv2 } from '../../components/DSGVOv2';
import { FahrtkostenvereinbarungGutachten } from '../../components/FahrtkostenvereinbarungGutachten';
import { FahrtkostenvereinbarungReparaturbestaetigung } from '../../components/FahrtkostenvereinbarungReparaturbestaetigung';
import { HIS } from '../../components/HIS';
import { Nachbesichtigung } from '../../components/Nachbesichtigung';
import { Regressansprueche } from '../../components/Regressansprueche';
import { Widerrufsbelehrung } from '../../components/Widerrufsbelehrung';
import { Zahlungsanweisung } from '../../components/Zahlungsanweisung';
import { useVorgangContext } from '../../contexts/vorgangContext';
import { makeGraphqlQuery } from '../../graphql/makeGraphqlQuery';
import * as mutations from '../../graphql/mutations';
import { useDateiUrl } from '../../hooks/useDateiUrl';
import { Gutachtenart, Unterzeichner } from '../../shared/constants';
import { S3Client } from '../../shared/s3Client';
import { sindAlleUnterschriftenVollstaendig } from '../../shared/unterschriftenHelper';
import { Unterschriften as UnterschriftenType } from '../../types';
import './Unterschriften.css';
import { SPACING_BETWEEN_BOXES } from '../../components/common/spacings';

export type UnterschriftenCheckboxState = {
  his: boolean;
  dsgvo: boolean;
  abtretungserklaerung: boolean;
  auftragsbestaetigung: boolean;
  dsgvoV2: boolean;
  auftragZurErstellungGutachten: boolean;
  auftragZurErstellungFahrzeugbewertung: boolean;
  auftragZurErstellungReparaturnachweis: boolean;
  fahrtkostenvereinbarungGutachten: boolean;
  fahrtkostenvereinbarungReparaturnachweis: boolean;
  regressansprueche: boolean;
  zahlungsanweisung: boolean;
  widerrufsbelehrung: boolean;
  angebotZurAbtretung: boolean;
};

export default function Unterschriften(): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { vorgang, isLoading, aktualisiereVorgang, setzeVorgang, speichereVorgang } = useVorgangContext();

  const [unterzeichnerAuswahlGlobal, setUnterzeichnerAuswahlGlobal] = useState(vorgang.unterschriften?.dsgvoV2?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER);
  const [unterzeichnerAuswahlNachbesichtigung, setUnterzeichnerAuswahlNachbesichtigung] = useState(
    vorgang.unterschriften?.nachbesichtigung?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER
  );
  const [signDate, setSignDate] = useState<string | undefined>(new Date().toISOString());
  const [signatureKey, setSignatureKey] = useState<string | undefined>(undefined);
  const [signatureKeyNachbesichtigung, setSignatureKeyNachbesichtigung] = useState<string | undefined>(undefined);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [mandatoryFieldsChecked, setMandatoryFieldsChecked] = useState<boolean>(false);
  const [checkboxState, setCheckboxState] = useState<UnterschriftenCheckboxState>({
    his: !!vorgang.unterschriften?.his?.key,
    dsgvo: !!vorgang.unterschriften?.dsgvo?.key,
    abtretungserklaerung: !!vorgang.unterschriften?.abtretungserklaerung?.key,
    auftragsbestaetigung: !!vorgang.unterschriften?.auftragsbestaetigung?.key,
    dsgvoV2: !!vorgang.unterschriften?.dsgvoV2?.key,
    auftragZurErstellungGutachten: !!vorgang.unterschriften?.auftragZurErstellungGutachten?.key,
    auftragZurErstellungReparaturnachweis: !!vorgang.unterschriften?.auftragZurErstellungReparaturnachweis?.key,
    auftragZurErstellungFahrzeugbewertung: !!vorgang.unterschriften?.auftragZurErstellungFahrzeugbewertung?.key,
    fahrtkostenvereinbarungGutachten: !!vorgang.unterschriften?.fahrtkostenvereinbarungGutachten?.key,
    fahrtkostenvereinbarungReparaturnachweis: !!vorgang.unterschriften?.fahrtkostenvereinbarungReparaturnachweis?.key,
    regressansprueche: !!vorgang.unterschriften?.regressansprueche?.key,
    zahlungsanweisung: !!vorgang.unterschriften?.zahlungsanweisung?.key,
    widerrufsbelehrung: !!vorgang.unterschriften?.widerrufsbelehrung?.key,
    angebotZurAbtretung: !!vorgang.unterschriften?.angebotZurAbtretung?.key
  });

  const [nachbesichtigungCheckbox, setNachbesichtigungCheckbox] = useState(!!vorgang.unterschriften?.nachbesichtigung?.key);

  const signatureUrl = useDateiUrl(signatureKey);

  const signatureUrlNachbesichtigung = useDateiUrl(signatureKeyNachbesichtigung);

  const pruefePflichtfelder = useCallback(() => {
    if (vorgang.agbV2) {
      setMandatoryFieldsChecked(
        ((((checkboxState.auftragZurErstellungGutachten && checkboxState.fahrtkostenvereinbarungGutachten) ||
          (checkboxState.auftragZurErstellungReparaturnachweis && checkboxState.fahrtkostenvereinbarungReparaturnachweis)) &&
          checkboxState.regressansprueche &&
          checkboxState.zahlungsanweisung &&
          checkboxState.angebotZurAbtretung) ||
          checkboxState.auftragZurErstellungFahrzeugbewertung) &&
          checkboxState.dsgvoV2 &&
          checkboxState.widerrufsbelehrung &&
          Boolean(vorgang.vertragsunterlagenAkzeptiert) &&
          Boolean(vorgang.digitaleUnterschriftAkzeptiert)
      );
    } else {
      setMandatoryFieldsChecked((checkboxState.abtretungserklaerung || checkboxState.auftragsbestaetigung) && checkboxState.dsgvo);
    }
  }, [
    checkboxState.abtretungserklaerung,
    checkboxState.auftragZurErstellungFahrzeugbewertung,
    checkboxState.auftragZurErstellungGutachten,
    checkboxState.auftragZurErstellungReparaturnachweis,
    checkboxState.auftragsbestaetigung,
    checkboxState.dsgvo,
    checkboxState.dsgvoV2,
    checkboxState.fahrtkostenvereinbarungGutachten,
    checkboxState.fahrtkostenvereinbarungReparaturnachweis,
    checkboxState.regressansprueche,
    checkboxState.widerrufsbelehrung,
    checkboxState.angebotZurAbtretung,
    checkboxState.zahlungsanweisung,
    vorgang.agbV2,
    vorgang.digitaleUnterschriftAkzeptiert,
    vorgang.vertragsunterlagenAkzeptiert
  ]);

  useEffect(() => {
    let found = false;
    for (const typ of Object.keys(checkboxState)) {
      if (vorgang.unterschriften?.[typ as keyof UnterschriftenType]?.key) {
        if (!found) {
          setSignDate(vorgang.unterschriften[typ as keyof UnterschriftenType]?.datum);
          setSignatureKey(vorgang.unterschriften[typ as keyof UnterschriftenType]?.key);
        }
        found = true;
      }
    }

    // Nachbesichtigung separat prüfen, da es nicht in checkboxState ist
    if (vorgang.unterschriften?.nachbesichtigung?.key) {
      setSignatureKeyNachbesichtigung(vorgang.unterschriften.nachbesichtigung?.key);
      if (!found) {
        setSignDate(vorgang.unterschriften.nachbesichtigung?.datum);
        found = true;
      }
    } else {
      setSignatureKeyNachbesichtigung(undefined);
    }

    if (!found) {
      setSignDate(new Date().toISOString());
      setSignatureKey(undefined);
    }

    pruefePflichtfelder();
  }, [checkboxState, vorgang.unterschriften, pruefePflichtfelder]);

  const handleSign = async (signature: string, datum: string) => {
    try {
      setIsUpdating(true);
      if (!mandatoryFieldsChecked) {
        enqueueSnackbar('Es wurde keiner AGB zugestimmt', ERROR_MESSAGE);
        return;
      }
      await speichereVorgang();
      for (const typ of Object.keys(checkboxState)) {
        if (checkboxState[typ as keyof UnterschriftenCheckboxState]) {
          await handleSignFor(typ as keyof UnterschriftenType)(signature, datum, unterzeichnerAuswahlGlobal);
        }
      }

      enqueueSnackbar('Die Unterschrift wurde erfolgreich gespeichert.', SUCCESS_MESSAGE_AUTO_HIDE);
    } catch (error) {
      console.error({ error });
      enqueueSnackbar('Die Unterschrift konnte nicht gespeichert werden.', ERROR_MESSAGE);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSignNachbesichtigung = async (signature: string, datum: string) => {
    try {
      setIsUpdating(true);
      if (!nachbesichtigungCheckbox) {
        enqueueSnackbar('Es wurde der AGB Nachbesichtigung nicht zugestimmt', ERROR_MESSAGE);
        return;
      }
      await handleSignFor('nachbesichtigung')(signature, datum, unterzeichnerAuswahlNachbesichtigung);

      enqueueSnackbar('Die Unterschrift wurde erfolgreich gespeichert.', SUCCESS_MESSAGE_AUTO_HIDE);
    } catch (error) {
      console.error({ error });
      enqueueSnackbar('Die Unterschrift konnte nicht gespeichert werden.', ERROR_MESSAGE);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSignFor = (typ: keyof UnterschriftenType) => async (signature: string, datum: string, unterzeichnerAuswahl: Unterzeichner) => {
    const response = await fetch(signature);
    const blob = await response.blob();
    const contentType = blob.type;
    const key = `${vorgang.id}/unterschriften/${typ}.png`;
    await S3Client.put(key, blob, contentType);
    const aktualisierterVorgang = await makeGraphqlQuery(mutations.leisteUnterschrift, {
      vorgangId: vorgang.id,
      typ,
      key,
      datum,
      unterzeichner: unterzeichnerAuswahl
    });
    setzeVorgang(aktualisierterVorgang);
  };

  const handleDeleteSignature = async () => {
    try {
      setIsUpdating(true);
      for (const typ of Object.keys(checkboxState)) {
        await S3Client.remove(`${vorgang.id}/unterschriften/${typ}.png`);
        const aktualisierterVorgang = await makeGraphqlQuery(mutations.entferneUnterschrift, {
          vorgangId: vorgang.id,
          typ
        });
        setzeVorgang(aktualisierterVorgang);
      }
      enqueueSnackbar('Die Unterschrift wurde erfolgreich entfernt.', SUCCESS_MESSAGE_AUTO_HIDE);
    } catch (error) {
      console.error({ error });
      enqueueSnackbar('Die Unterschrift konnte nicht entfernt werden.', ERROR_MESSAGE);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteSignatureNachbesichtigung = async () => {
    try {
      setIsUpdating(true);
      await S3Client.remove(`${vorgang.id}/unterschriften/nachbesichtigung.png`);
      const aktualisierterVorgang = await makeGraphqlQuery(mutations.entferneUnterschrift, {
        vorgangId: vorgang.id,
        typ: 'nachbesichtigung'
      });
      setzeVorgang(aktualisierterVorgang);
      enqueueSnackbar('Die Unterschrift wurde erfolgreich entfernt.', SUCCESS_MESSAGE_AUTO_HIDE);
    } catch (error) {
      console.error({ error });
      enqueueSnackbar('Die Unterschrift konnte nicht entfernt werden.', ERROR_MESSAGE);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateUnterzeichner = (unterzeichner: Unterzeichner) => {
    setIsUpdating(true);
    for (const typ of Object.keys(checkboxState)) {
      if (vorgang.unterschriften?.[typ as keyof UnterschriftenType]?.key) {
        aktualisiereVorgang({
          unterschriften: {
            ...vorgang.unterschriften,
            [typ]: {
              ...(vorgang.unterschriften ? vorgang.unterschriften[typ as keyof UnterschriftenType] : {}),
              unterzeichner
            }
          }
        });
      }
    }
    setIsUpdating(false);
  };

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return !sindAlleUnterschriftenVollstaendig(vorgang);
  });

  function updateCheckboxState(newState: Partial<UnterschriftenCheckboxState>) {
    setCheckboxState((current) => {
      return { ...current, ...newState };
    });
  }

  function updateAllCheckboxStates(checked: boolean) {
    setCheckboxState((state) => {
      Object.keys(state).forEach((key: string) => {
        state[key as keyof UnterschriftenCheckboxState] = checked;
      });

      return state;
    });
  }

  return (
    <>
      <WarningDialog
        open={blocker.state === 'blocked'}
        onAbort={() => {
          blocker.reset?.();
        }}
        text="Es wurden noch nicht alle Unterschriften bestätigt. Bitte prüfen Sie, ob alle Unterschriften abgegeben wurden."
        title="Nachbesichtigung erforderlich?"
        onChooseYes={() => {
          blocker.proceed?.();
        }}
        confirmText="Weiter"
        abortText="Abbrechen"
      />
      <Grid container spacing={SPACING_BETWEEN_BOXES}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Formular ueberschrift="Unterschriften">
            {vorgang.agbV2 ? (
              <>
                {(vorgang.gutachtenart === Gutachtenart.KURZGUTACHTEN ||
                  vorgang.gutachtenart === Gutachtenart.SCHADENSGUTACHTEN_HAFTPFLICHT ||
                  vorgang.gutachtenart === Gutachtenart.SCHADENSGUTACHTEN_KASKO) && (
                  <>
                    <AuftragZurErstellungGutachten
                      vorgang={vorgang}
                      isLoading={isLoading}
                      checkboxState={checkboxState}
                      updateCheckboxState={updateCheckboxState}
                    />
                    <FahrtkostenvereinbarungGutachten
                      vorgang={vorgang}
                      isLoading={isLoading}
                      checkboxState={checkboxState}
                      updateCheckboxState={updateCheckboxState}
                    />
                    <Regressansprueche vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                    <Zahlungsanweisung vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                    <AngebotZurAbtretung vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                  </>
                )}
                {vorgang.gutachtenart === Gutachtenart.REPARATURNACHWEIS && (
                  <>
                    <AuftragZurErstellungReparaturbestaetigung
                      vorgang={vorgang}
                      isLoading={isLoading}
                      checkboxState={checkboxState}
                      updateCheckboxState={updateCheckboxState}
                    />
                    <FahrtkostenvereinbarungReparaturbestaetigung
                      vorgang={vorgang}
                      isLoading={isLoading}
                      checkboxState={checkboxState}
                      updateCheckboxState={updateCheckboxState}
                    />
                    <Regressansprueche vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                    <Zahlungsanweisung vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                    <AngebotZurAbtretung vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                  </>
                )}
                {vorgang.gutachtenart === Gutachtenart.FAHRZEUGBEWERTUNG && (
                  <AuftragZurErstellungFahrzeugbewertung
                    vorgang={vorgang}
                    isLoading={isLoading}
                    checkboxState={checkboxState}
                    updateCheckboxState={updateCheckboxState}
                  />
                )}
                <>
                  <DSGVOv2 vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                  <Widerrufsbelehrung vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                  {vorgang?.gutachtenart !== Gutachtenart.REPARATURNACHWEIS && (
                    <>
                      <HIS
                        vorgang={vorgang}
                        isLoading={isLoading}
                        setzeVorgang={setzeVorgang}
                        checkboxState={checkboxState}
                        updateCheckboxState={updateCheckboxState}
                      />
                      <br />
                    </>
                  )}
                </>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={vorgang.vertragsunterlagenAkzeptiert ?? false}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        updateAllCheckboxStates(event.target.checked);
                        aktualisiereVorgang({ vertragsunterlagenAkzeptiert: event.target.checked });
                      }}
                      name="vertragsunterlagenAkzeptiert"
                      color="primary"
                      data-testid="vertragsunterlagenAkzeptiertCheckbox"
                    />
                  }
                  label="Ich habe alle o.g. Vertragsunterlagen der Unternehmen zur Kenntnis genommen und bin mit deren Geltung einverstanden. Es sollen alle Opt-In Kästchen aktiviert werden.*"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={vorgang.digitaleUnterschriftAkzeptiert ?? false}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        aktualisiereVorgang({ digitaleUnterschriftAkzeptiert: event.target.checked });
                      }}
                      name="digitaleUnterschriftAkzeptiert"
                      color="primary"
                      data-testid="digitaleUnterschriftAkzeptiertCheckbox"
                    />
                  }
                  label="Ich bin damit einverstanden, dass meine digitale Unterschrift unter alle o.g. Vertragsunterlagen kopiert wird. Ich habe verstanden, dass ich dadurch verbindliche Vertragsangebote abgebe und Verträge abschließe, meine Kenntnisnahme bestätige, Erklärungen übermittle und das Unternehmen meine Unterschrift als elektronische Unterschrift akzeptiert.*
"
                />
              </>
            ) : (
              <>
                <DSGVO vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                {vorgang?.gutachtenart === Gutachtenart.FAHRZEUGBEWERTUNG ? (
                  <Auftragsbestaetigung vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                ) : (
                  <Abtretungserklaerung vorgang={vorgang} isLoading={isLoading} checkboxState={checkboxState} updateCheckboxState={updateCheckboxState} />
                )}
                {vorgang?.gutachtenart !== Gutachtenart.REPARATURNACHWEIS && (
                  <>
                    <HIS
                      vorgang={vorgang}
                      isLoading={isLoading}
                      setzeVorgang={setzeVorgang}
                      checkboxState={checkboxState}
                      updateCheckboxState={updateCheckboxState}
                    />
                    <br />
                  </>
                )}
              </>
            )}
            <br />

            <h6>* Pflichtfelder</h6>

            <br />
            {isUpdating ? (
              <div className="signature__skeleton"></div>
            ) : (
              <Signature
                onSign={handleSign}
                onDeleteSignature={handleDeleteSignature}
                signatureUrl={signatureUrl}
                isLoading={isLoading}
                vorgang={vorgang}
                unterzeichnerAuswahl={unterzeichnerAuswahlGlobal}
                setUnterzeichnerAuswahl={(unterzeichner) => {
                  setUnterzeichnerAuswahlGlobal(unterzeichner);
                }}
                updateUnterzeichner={updateUnterzeichner}
                date={signDate}
                mandatoryFieldsChecked={mandatoryFieldsChecked}
                dataTestid="hauptunterschrift"
              />
            )}
            {(vorgang.gutachtenart === Gutachtenart.KURZGUTACHTEN ||
              vorgang.gutachtenart === Gutachtenart.SCHADENSGUTACHTEN_HAFTPFLICHT ||
              vorgang.gutachtenart === Gutachtenart.REPARATURNACHWEIS ||
              vorgang.gutachtenart === Gutachtenart.SCHADENSGUTACHTEN_KASKO) && (
              <div className="unterschriften__small-text-box_hint">
                <b>Hinweis:</b> Wenn Sie Geschädigter des Verkehrsunfalls sind, ist die Haftpflichtversicherung des am Unfall beteiligten, gegnerischen
                Fahrzeugs für die Kosten der Reparatur, der Gutachtenerstellung und Rechtsdienstleistung einstandspflichtig. Sind Sie Verursacher des
                Verkehrsunfalls, müssen Sie diese Kosten selbst tragen. Es wird darüber aufgeklärt, dass der Haftpflichtversicherer möglicherweise die
                vereinbarten Entgelte nicht in vollem Umfang erstattet, mit dem Argument, dass diese von den ortsüblichen Entgelten deutlich abweichen.
              </div>
            )}
          </Formular>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Formular ueberschrift="Unterschrift Nachbesichtigung">
            {(vorgang.gutachtenart === Gutachtenart.KURZGUTACHTEN ||
              vorgang.gutachtenart === Gutachtenart.SCHADENSGUTACHTEN_HAFTPFLICHT ||
              vorgang.gutachtenart === Gutachtenart.SCHADENSGUTACHTEN_KASKO) && (
              <>
                <Nachbesichtigung
                  vorgang={vorgang}
                  isLoading={isLoading}
                  checkboxState={nachbesichtigungCheckbox}
                  updateCheckboxState={setNachbesichtigungCheckbox}
                />
                {isUpdating ? (
                  <div className="signature__skeleton"></div>
                ) : (
                  <Signature
                    onSign={handleSignNachbesichtigung}
                    onDeleteSignature={handleDeleteSignatureNachbesichtigung}
                    signatureUrl={signatureUrlNachbesichtigung}
                    isLoading={isLoading}
                    vorgang={vorgang}
                    unterzeichnerAuswahl={unterzeichnerAuswahlNachbesichtigung}
                    setUnterzeichnerAuswahl={(unterzeichner) => {
                      setUnterzeichnerAuswahlNachbesichtigung(unterzeichner);
                    }}
                    updateUnterzeichner={updateUnterzeichner}
                    date={signDate}
                    mandatoryFieldsChecked={nachbesichtigungCheckbox}
                    readOnly={!nachbesichtigungCheckbox && !!signatureUrlNachbesichtigung}
                    dataTestid="nachbesichtigungUnterschrift"
                  />
                )}
              </>
            )}
          </Formular>
        </Grid>
      </Grid>
    </>
  );
}
